import { useApolloClient } from '@apollo/client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    AdminPostView,
    ContentSearchParams,
    GetPostConversationPageQuery,
    PostConversationReplyItemFragment,
    PostRefreshTaskItemFragment,
    PostRefreshTaskStatus,
    GetMoreThreadRepliesDocument,
    GetMoreThreadRepliesQuery,
    GetMoreThreadRepliesQueryVariables,
    useGetPostConversationPageQuery,
    usePostConversationRefreshTaskLazyQuery,
    useStartPostConversationRefreshMutation
} from '../../api/client'
import { graphqlErrorMessage } from '../../util/graphqlErrorMessage'
import { toastError } from '../Notification'
import SearchParameters, { mapToContentListSearchParams } from '../content/search/searchParameters'

export type PostConversationItem =
    GetPostConversationPageQuery['getPostConversationPage']['items'][number]
export type PostConversationState = 'loading' | 'ready' | 'error'

export const POST_CONVERSATION_PAGE_SIZE = 20
export const THREAD_REPLIES_PAGE_SIZE = 10
// The page catches up with the pipeline by re-reading itself (a full reset, like
// the first read) at this interval: once after a refresh, because YouTube's
// replies and parent links arrive through the pipeline after the mutation
// returns; and for as long as it shows comments awaiting moderation, because a
// refresh stores comments the classifiers take a minute or two to get through,
// and until they do the feed's default filter hides them.
export const CATCH_UP_INTERVAL_MS = 20_000
// Reads per catch-up before giving up, so a comment the pipeline never
// moderates does not keep the tab polling for the rest of the session.
export const CATCH_UP_MAX_READS = 9
// A refresh fetches a whole post from the platform, which can take longer than
// a request may last, so the server runs it as a task and the page polls for
// the result at this interval, giving up after REFRESH_POLL_MAX_MS.
export const REFRESH_POLL_INTERVAL_MS = 1_000
export const REFRESH_POLL_MAX_MS = 3 * 60 * 1_000

const REFRESH_FAILED = "Couldn't check for new comments. Showing what Arwen holds."

const isSettled = (task: PostRefreshTaskItemFragment) =>
    task.status === PostRefreshTaskStatus.Completed || task.status === PostRefreshTaskStatus.Failed

const isGap = (item: PostConversationItem) => !item.thread && item.gapCount != null

/** Something on screen is excluded only because it has no moderation yet. */
export const awaitsModeration = (item: PostConversationItem) =>
    (item.gapAwaitingModerationCount ?? 0) > 0 ||
    !!item.thread?.rootAwaitingModeration ||
    (item.thread?.repliesAwaitingModerationCount ?? 0) > 0

/**
 * Append one page of items to those already shown. The server cuts pages by
 * scanned roots, so a run of filtered roots can straddle the cut and arrive as
 * a trailing gap on one page and a leading gap on the next; those merge into
 * one marker so the count reads as the server would have given it unpaged.
 */
export function appendItems(
    current: PostConversationItem[],
    page: PostConversationItem[]
): PostConversationItem[] {
    // A root already on screen is never shown twice. Every refetch of page one resets
    // the appended pages, so this is a guard only, against a server page that
    // overlaps the one before it.
    const shown = new Set(current.flatMap((item) => (item.thread ? [item.thread.root.id] : [])))
    page = page.filter((item) => !item.thread || !shown.has(item.thread.root.id))
    const last = current[current.length - 1]
    const first = page[0]
    if (last && first && isGap(last) && isGap(first)) {
        return [
            ...current.slice(0, -1),
            {
                ...last,
                gapCount: (last.gapCount ?? 0) + (first.gapCount ?? 0),
                gapAwaitingModerationCount:
                    (last.gapAwaitingModerationCount ?? 0) + (first.gapAwaitingModerationCount ?? 0)
            },
            ...page.slice(1)
        ]
    }
    return [...current, ...page]
}

type ExtraPage = { items: PostConversationItem[]; nextCursor: string | null }
const NO_PAGES: ExtraPage[] = []

/**
 * The Threaded tab's data. Page one is the live query, so a
 * cache update to it flows through; the pages "Show more comments" adds are
 * kept beside it rather than merged into the cache, which has no field policy
 * for this query. Both are keyed by the filters, so changing a filter starts
 * again from the newest comment without an effect to clear anything.
 */
export function usePostConversationPage(
    post: Pick<AdminPostView, 'id'>,
    searchParameters: SearchParameters
) {
    // Keyed on the serialised filters, not the object, so a parent that rebuilds
    // an equal SearchParameters does not refetch.
    const mapped = mapToContentListSearchParams(searchParameters)
    const paramsKey = JSON.stringify(mapped)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const params: ContentSearchParams = useMemo(() => mapped, [paramsKey])

    const { data, error, refetch, fetchMore } = useGetPostConversationPageQuery({
        variables: { params, after: null, limit: POST_CONVERSATION_PAGE_SIZE },
        fetchPolicy: 'network-only'
    })
    const [startRefresh] = useStartPostConversationRefreshMutation()
    const [readRefreshTask] = usePostConversationRefreshTaskLazyQuery({ fetchPolicy: 'no-cache' })

    // Bumped when a refresh replaces the list, so per-thread reply pages taken
    // from the old list are dropped too.
    const [generation, setGeneration] = useState(0)
    const generationRef = useRef(generation)
    const resetKey = `${generation}|${paramsKey}`
    const resetKeyRef = useRef(resetKey)
    resetKeyRef.current = resetKey
    const paramsKeyRef = useRef(paramsKey)
    paramsKeyRef.current = paramsKey

    const [more, setMore] = useState<{ key: string; pages: ExtraPage[] }>({
        key: resetKey,
        pages: NO_PAGES
    })
    const extraPages = more.key === resetKey ? more.pages : NO_PAGES
    const [loadingMore, setLoadingMore] = useState(false)
    const [fetching, setFetching] = useState(false)
    // Non-fatal problems from the last refresh, in the server's plain English.
    // About the post, not the filters, so a filter change keeps them; a new
    // refresh replaces them.
    const [notices, setNotices] = useState<string[]>([])
    // What the last refresh that ran found, for the list it produced (`key`).
    const [found, setFound] = useState<{
        key: string
        newComments: number
        platform: string
    } | null>(null)

    const firstPage = data?.getPostConversationPage
    const items = useMemo(
        () =>
            extraPages.reduce((acc, page) => appendItems(acc, page.items), firstPage?.items ?? []),
        [firstPage, extraPages]
    )
    const nextCursor =
        extraPages.length > 0
            ? extraPages[extraPages.length - 1].nextCursor
            : (firstPage?.nextCursor ?? null)

    const loadMore = useCallback(async () => {
        if (loadingMore || !nextCursor) return
        const key = resetKey
        setLoadingMore(true)
        try {
            const result = await fetchMore({
                variables: { params, after: nextCursor, limit: POST_CONVERSATION_PAGE_SIZE }
            })
            // The filters changed or a refresh landed while this page was in flight;
            // it belongs to a list that is no longer on screen.
            if (key !== resetKeyRef.current) return
            const page = result.data.getPostConversationPage
            setMore((current) => ({
                key,
                pages: [
                    ...(current.key === key ? current.pages : NO_PAGES),
                    { items: page.items, nextCursor: page.nextCursor ?? null }
                ]
            }))
        } catch (err) {
            console.error('Error loading more post conversation threads:', err)
            toastError("Couldn't load more comments. Try again.")
        } finally {
            setLoadingMore(false)
        }
    }, [loadingMore, nextCursor, resetKey, fetchMore, params])

    // Reset the list and read page one again from the top. A new generation drops the
    // "Show more comments" pages and per-thread reply pages: their cursors chain from
    // the old page one, so keeping them beside a new page one would lose or double
    // count roots wherever the two disagree. Bumped before the read, so no render
    // shows the new page one beside the old appended pages. Returns the new key.
    const resetAndRefetch = useCallback(async () => {
        const nextGeneration = generationRef.current + 1
        generationRef.current = nextGeneration
        setGeneration(nextGeneration)
        const key = `${nextGeneration}|${paramsKeyRef.current}`
        await refetch()
        return key
    }, [refetch])

    // Catching up with the pipeline (see CATCH_UP_INTERVAL_MS). A read is due when
    // a refresh just ran (one read, for the key it produced) or while the list shows
    // comments awaiting moderation. Each read resets the list, which changes the
    // key, so the effect below re-evaluates after every read; the count of reads
    // is kept per filters, and a refresh starts it again.
    const awaitingModeration = useMemo(() => items.some(awaitsModeration), [items])
    const [readDueFor, setReadDueFor] = useState<string | null>(null)
    const catchUp = useRef({ paramsKey, reads: 0 })
    if (catchUp.current.paramsKey !== paramsKey) catchUp.current = { paramsKey, reads: 0 }
    const awaitingLatest =
        (readDueFor === resetKey || awaitingModeration) &&
        catchUp.current.reads < CATCH_UP_MAX_READS
    useEffect(() => {
        if (!awaitingLatest) return
        const key = resetKey
        const timer = setTimeout(async () => {
            // The cleanup covers a key change, but a late tick costs nothing to check.
            if (key !== resetKeyRef.current) return
            catchUp.current.reads += 1
            setReadDueFor(null)
            try {
                await resetAndRefetch()
            } catch (err) {
                console.error('Error in the catch-up post conversation refetch:', err)
            }
        }, CATCH_UP_INTERVAL_MS)
        // A filter change or another refresh produced a newer list; this read is for
        // the old one, so it goes, and the new list schedules its own if it needs one.
        return () => clearTimeout(timer)
    }, [awaitingLatest, resetKey, resetAndRefetch])

    // Starts the refresh and waits for its task to settle: the start returns the
    // result inline when the refresh was quick, otherwise the task is polled.
    const runRefresh = useCallback(async (): Promise<PostRefreshTaskItemFragment> => {
        const started = await startRefresh({ variables: { postId: post.id } })
        const start = started.data?.startPostConversationRefresh
        if (!start) throw new Error('The refresh did not start')
        if (start.result && isSettled(start.result)) return start.result
        const deadline = Date.now() + REFRESH_POLL_MAX_MS
        while (Date.now() < deadline) {
            await new Promise((resolve) => setTimeout(resolve, REFRESH_POLL_INTERVAL_MS))
            const polled = await readRefreshTask({ variables: { taskId: start.taskId } })
            const task = polled.data?.postConversationRefreshTask
            if (task && isSettled(task)) return task
        }
        throw new Error(
            'The refresh is taking too long. Reload the page in a minute to see the latest.'
        )
    }, [post.id, startRefresh, readRefreshTask])

    const refresh = useCallback(async () => {
        setFetching(true)
        setNotices([])
        setFound(null)
        let ran = false
        let newComments = 0
        let platform = ''
        try {
            const task = await runRefresh()
            if (task.status === PostRefreshTaskStatus.Failed) {
                // The server words the failure for the moderator, e.g. that the
                // account needs re-authorising, which a generic message would hide.
                toastError(task.error ?? REFRESH_FAILED)
                setFetching(false)
                return
            }
            setNotices(task.outcome?.notices ?? [])
            ran = !!task.outcome?.ran
            newComments = task.outcome?.newComments ?? 0
            platform = task.outcome?.platform ?? ''
        } catch (err) {
            // The server takes the cooldown even when the platform call fails, so the
            // page is left as it was rather than retried (spec, "Error handling").
            console.error('Error refreshing post conversation:', err)
            toastError(graphqlErrorMessage(err, REFRESH_FAILED))
            setFetching(false)
            return
        }
        let key: string | null = null
        try {
            // From the top: new comments land at the head, and replies may have joined
            // threads already on screen.
            key = await resetAndRefetch()
        } catch (err) {
            console.error('Error reloading post conversation after refresh:', err)
            // The moderator has been told to reload, so no delayed read follows.
            toastError("Couldn't reload the comments. Reload the page to see the latest.")
        } finally {
            setFetching(false)
        }
        // The platform was asked, so what it returned is working through the
        // pipeline: a fresh catch-up, with one read owed whatever is on screen.
        if (ran && key) {
            catchUp.current = { paramsKey: paramsKeyRef.current, reads: 0 }
            setReadDueFor(key)
            setFound({ key, newComments, platform })
        }
    }, [runRefresh, resetAndRefetch])

    // Ask the platform once per post per visit, and only when the server says it
    // can (platform supported, cooldown clear).
    const canRefresh = firstPage?.canRefresh ?? false
    const autoRefreshedFor = useRef<number | null>(null)
    useEffect(() => {
        if (canRefresh && autoRefreshedFor.current !== post.id) {
            autoRefreshedFor.current = post.id
            void refresh()
        }
    }, [canRefresh, post.id, refresh])

    let state: PostConversationState = 'ready'
    if (!firstPage) state = error ? 'error' : 'loading'

    // Said only until the read the refresh owes: by then the new comments have
    // had time to be moderated, so a count would read as still to come.
    const refreshFound =
        found && found.key === resetKey && readDueFor === resetKey
            ? { newComments: found.newComments, platform: found.platform }
            : null

    return {
        items,
        nextCursor,
        matchingTotal: firstPage?.matchingTotal ?? 0,
        matchingCapped: firstPage?.matchingCapped ?? false,
        canRefresh,
        state,
        loadMore,
        loadingMore,
        refresh,
        fetching,
        awaitingLatest,
        awaitingModeration,
        refreshFound,
        notices,
        params,
        resetKey
    }
}

type ThreadPage = {
    // Null until the first page lands: until then the thread shows the preview.
    replies: PostConversationReplyItemFragment[] | null
    nextCursor: string | null
    loading: boolean
}
const NO_THREAD_PAGES = new Map<number, ThreadPage>()

/**
 * "View N more replies" for every thread on the tab, keyed by root id.
 *
 * The preview is not a prefix of the thread (it is the first matching replies
 * plus their ancestors), and `getMoreThreadReplies` has no cursor that continues it.
 * So the first page, fetched with `after: null`, starts from the thread's first
 * reply and REPLACES the preview; later pages append. Reply gaps are worked out
 * from the `matches` flags at render, so they need no merging across pages.
 *
 * Each call goes through the client rather than one shared lazy query, so two
 * threads loading at once cannot answer each other's promise.
 */
export function useMoreThreadReplies(params: ContentSearchParams, resetKey: string) {
    const client = useApolloClient()
    const [state, setState] = useState<{ key: string; byRoot: Map<number, ThreadPage> }>({
        key: resetKey,
        byRoot: NO_THREAD_PAGES
    })
    const byRoot = state.key === resetKey ? state.byRoot : NO_THREAD_PAGES
    const resetKeyRef = useRef(resetKey)
    resetKeyRef.current = resetKey

    const patch = useCallback(
        (key: string, rootId: number, update: (page: ThreadPage | undefined) => ThreadPage) => {
            setState((current) => {
                // A late answer for a list that has since been reset is dropped.
                if (key !== resetKeyRef.current) return current
                const next = new Map(current.key === key ? current.byRoot : NO_THREAD_PAGES)
                next.set(rootId, update(next.get(rootId)))
                return { key, byRoot: next }
            })
        },
        []
    )

    const loadMoreReplies = useCallback(
        async (rootId: number) => {
            const existing = byRoot.get(rootId)
            if (existing?.loading) return
            // Paged to the end already.
            if (existing?.replies && existing.nextCursor === null) return
            const after = existing?.replies ? existing.nextCursor : null
            const key = resetKey
            patch(key, rootId, (page) => ({
                replies: page?.replies ?? null,
                nextCursor: page?.nextCursor ?? null,
                loading: true
            }))
            try {
                const result = await client.query<
                    GetMoreThreadRepliesQuery,
                    GetMoreThreadRepliesQueryVariables
                >({
                    query: GetMoreThreadRepliesDocument,
                    variables: {
                        contentId: rootId,
                        params,
                        after,
                        limit: THREAD_REPLIES_PAGE_SIZE
                    },
                    fetchPolicy: 'network-only'
                })
                const page = result.data.getMoreThreadReplies
                patch(key, rootId, (current) => ({
                    replies:
                        after === null
                            ? page.replies
                            : [...(current?.replies ?? []), ...page.replies],
                    nextCursor: page.nextCursor ?? null,
                    loading: false
                }))
            } catch (err) {
                console.error('Error loading thread replies:', err)
                toastError("Couldn't load more replies. Try again.")
                patch(key, rootId, (current) => ({
                    replies: current?.replies ?? null,
                    nextCursor: current?.nextCursor ?? null,
                    loading: false
                }))
            }
        },
        [byRoot, resetKey, patch, client, params]
    )

    return { byRoot, loadMoreReplies }
}
